/**
 * useServicesStatus - Hook pour suivre l'état de tous les services
 *
 * Statuts possibles:
 * - 'idle': pas encore testé
 * - 'loading': en cours de connexion/test
 * - 'connected': connecté et fonctionnel
 * - 'error': erreur de connexion
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import googleAuthService from '../services/googleAuth';

// Clé API Groq pour la génération IA
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';

export function useServicesStatus() {
  const [statuses, setStatuses] = useState({
    groq: { status: 'idle', message: '', lastCheck: null },
    calendar: { status: 'idle', message: '', lastCheck: null },
    firebase: { status: 'idle', message: '', lastCheck: null },
    google: { status: 'idle', message: '', lastCheck: null },
  });

  // Mettre à jour un statut
  const updateStatus = useCallback((service, status, message = '') => {
    setStatuses(prev => ({
      ...prev,
      [service]: {
        status,
        message,
        lastCheck: new Date().toISOString()
      }
    }));
  }, []);

  // Tester la connexion Groq
  const checkGroq = useCallback(async () => {
    if (!GROQ_API_KEY) {
      updateStatus('groq', 'error', 'Clé API manquante');
      return false;
    }

    updateStatus('groq', 'loading', 'Test en cours...');

    try {
      const response = await fetch(GROQ_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${GROQ_API_KEY}`
        },
        body: JSON.stringify({
          model: 'llama-3.1-8b-instant',
          messages: [{ role: 'user', content: 'Test: réponds OK' }],
          max_tokens: 10
        })
      });

      if (response.ok) {
        updateStatus('groq', 'connected', 'Groq connecté');
        return true;
      } else {
        const error = await response.json().catch(() => ({}));
        updateStatus('groq', 'error', error?.error?.message || `Erreur ${response.status}`);
        return false;
      }
    } catch (e) {
      updateStatus('groq', 'error', e.message);
      return false;
    }
  }, [updateStatus]);

  // Vérifier le token OAuth Google
  const checkGoogle = useCallback(() => {
    const tokenStatus = googleAuthService.getTokenStatus();
    const map = {
      none:      ['idle',    'Non connecté'],
      connected: ['connected', 'Session active'],
      expiring:  ['loading', 'Expire bientôt (< 5 min)'],
      expired:   ['error',   'Session expirée — reconnexion requise'],
    };
    const [status, message] = map[tokenStatus] ?? ['idle', ''];
    updateStatus('google', status, message);
    return status !== 'error';
  }, [updateStatus]);

  // Vérifier Google Calendar
  const checkCalendar = useCallback(() => {
    const hasGapi = !!window.gapi?.client;
    const hasToken = !!window.gapi?.client?.getToken?.();

    if (!hasGapi) {
      updateStatus('calendar', 'idle', 'GAPI non chargé');
      return false;
    }

    if (!hasToken) {
      updateStatus('calendar', 'idle', 'Non connecté');
      return false;
    }

    // Vérifier si calendar API est disponible
    if (window.gapi?.client?.calendar) {
      updateStatus('calendar', 'connected', 'Calendar connecté');
      return true;
    } else {
      updateStatus('calendar', 'error', 'API Calendar non activée');
      return false;
    }
  }, [updateStatus]);

  // Vérifier Firebase
  const checkFirebase = useCallback(() => {
    try {
      // Vérifier si Firebase est initialisé
      const hasFirebase = typeof window !== 'undefined' &&
        (window.firebase || (typeof require !== 'undefined'));

      // Simple check - si on peut accéder à Firestore
      const firebaseConfig = process.env.REACT_APP_FIREBASE_PROJECT_ID;

      if (firebaseConfig) {
        updateStatus('firebase', 'connected', 'Firebase configuré');
        return true;
      } else {
        updateStatus('firebase', 'error', 'Config manquante');
        return false;
      }
    } catch (e) {
      updateStatus('firebase', 'error', e.message);
      return false;
    }
  }, [updateStatus]);

  // Vérifier tous les services
  const checkAllServices = useCallback(async () => {
    await checkGroq();
    checkCalendar();
    checkFirebase();
    checkGoogle();
  }, [checkGroq, checkCalendar, checkFirebase, checkGoogle]);

  // Vérification initiale au montage
  useEffect(() => {
    // Délai pour laisser les services s'initialiser
    const timer = setTimeout(() => {
      checkAllServices();
    }, 2000);

    return () => clearTimeout(timer);
  }, [checkAllServices]);

  // Écouter les changements de connexion Google (Calendar + token OAuth)
  useEffect(() => {
    const checkInterval = setInterval(() => {
      checkCalendar();
      checkGoogle();
    }, 30000); // Vérifier toutes les 30 secondes

    return () => clearInterval(checkInterval);
  }, [checkCalendar, checkGoogle]);

  // Calculer le statut global
  const globalStatus = useMemo(() => {
    const values = Object.values(statuses);
    if (values.some(s => s.status === 'error')) return 'error';
    if (values.some(s => s.status === 'loading')) return 'loading';
    if (values.every(s => s.status === 'connected')) return 'connected';
    return 'idle';
  }, [statuses]);

  return {
    statuses,
    globalStatus,
    checkGroq,
    checkCalendar,
    checkFirebase,
    checkGoogle,
    checkAllServices,
    updateStatus,
    apiKey: GROQ_API_KEY ? '✓' : '✗'
  };
}

/**
 * Composant d'indicateur de statut
 */
export function ServiceStatusIndicator({ status, size = 'sm', showLabel = false, label = '' }) {
  const sizeClasses = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3'
  };

  const statusConfig = {
    idle: { color: 'bg-zinc-500', label: 'Inactif' },
    loading: { color: 'bg-amber-500 animate-pulse', label: 'Chargement...' },
    connected: { color: 'bg-emerald-500', label: 'Connecté' },
    error: { color: 'bg-red-500', label: 'Erreur' }
  };

  const config = statusConfig[status] || statusConfig.idle;

  return (
    <div className="flex items-center gap-1">
      <div className={`${sizeClasses[size]} ${config.color} rounded-full`} />
      {showLabel && (
        <span className="text-[10px] text-zinc-500">
          {label || config.label}
        </span>
      )}
    </div>
  );
}

/**
 * Barre de statut des services (compacte)
 */
export function ServicesStatusBar({ statuses, compact = true }) {
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <ServiceStatusIcon service="groq" status={statuses.groq?.status} />
        <ServiceStatusIcon service="calendar" status={statuses.calendar?.status} />
        <ServiceStatusIcon service="firebase" status={statuses.firebase?.status} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-xs">
      <ServiceStatusBadge
        icon="⚡"
        label="Groq"
        status={statuses.groq?.status}
        message={statuses.groq?.message}
      />
      <ServiceStatusBadge
        icon="📅"
        label="Calendar"
        status={statuses.calendar?.status}
        message={statuses.calendar?.message}
      />
      <ServiceStatusBadge
        icon="🔥"
        label="Firebase"
        status={statuses.firebase?.status}
        message={statuses.firebase?.message}
      />
    </div>
  );
}

/**
 * Icône de statut avec emoji
 */
function ServiceStatusIcon({ service, status }) {
  const icons = {
    groq: '⚡',
    calendar: '📅',
    firebase: '🔥'
  };

  const statusColors = {
    idle: 'opacity-30',
    loading: 'animate-pulse',
    connected: '',
    error: 'grayscale'
  };

  const statusIndicator = {
    idle: '',
    loading: '🟡',
    connected: '🟢',
    error: '🔴'
  };

  return (
    <div className={`relative ${statusColors[status] || ''}`} title={`${service}: ${status}`}>
      <span className="text-sm">{icons[service]}</span>
      <span className="absolute -bottom-0.5 -right-0.5 text-[8px]">
        {statusIndicator[status]}
      </span>
    </div>
  );
}

/**
 * Badge de statut détaillé
 */
function ServiceStatusBadge({ icon, label, status, message }) {
  const statusStyles = {
    idle: 'bg-zinc-800/50 text-zinc-500',
    loading: 'bg-amber-500/20 text-amber-400',
    connected: 'bg-emerald-500/20 text-emerald-400',
    error: 'bg-red-500/20 text-red-400'
  };

  return (
    <div
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${statusStyles[status] || statusStyles.idle}`}
      title={message}
    >
      <span className="text-xs">{icon}</span>
      <span className="text-[10px] font-medium">{label}</span>
      <ServiceStatusIndicator status={status} size="xs" />
    </div>
  );
}

export default useServicesStatus;
